import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  getCertificadoFiscal,
  getConfiguracao,
  updateConfiguracao,
  uploadCertificadoFiscal,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';

const GALERIA_MAX_ITENS = 10;
const GALERIA_MAX_IMAGEM_MB = 5;
const GALERIA_MAX_VIDEO_MB = 40;

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|mkv|m3u8)(\?|$)/i;

function detectarTipoMidia(url) {
  const u = String(url || '').trim();
  if (/^data:video\//i.test(u) || VIDEO_EXT.test(u)) return 'video';
  return 'imagem';
}

function parseGaleria(json, imagemLegado) {
  let itens = [];
  if (json) {
    try {
      const parsed = typeof json === 'string' ? JSON.parse(json) : json;
      if (Array.isArray(parsed)) {
        itens = parsed
          .map((item) => ({
            url: String(item?.url || '').trim(),
            tipo: item?.tipo === 'video' ? 'video' : detectarTipoMidia(item?.url),
          }))
          .filter((item) => item.url);
      }
    } catch {
      itens = [];
    }
  }
  if (!itens.length && imagemLegado?.trim()) {
    const url = imagemLegado.trim();
    itens = [{ url, tipo: detectarTipoMidia(url) }];
  }
  return itens;
}

const emptyForm = () => ({
  nfceSerie: 1,
  nfceSequencia: 1,
  nfceTipoAmbiente: 2,
  nfceIdToken: '',
  nfceCsc: '',
  nfeSerie: 1,
  nfeSequencia: 1,
  nfeTipoAmbiente: 2,
  emiteNfePj: false,
  enviaEmailNfe: false,
  enviaXmlAutomaticamente: false,
  emailContador: '',
  diaEnvioXml: 1,
  cnpjCredenciadora: '',
  balancaQtdDigitos: 6,
  balancaTipoCodigo: 0,
  integradoZenTag: false,
  clientIdZenTag: '',
  clientSecretZenTag: '',
  tema: 'Azul',
  logoSistemaBase64: null,
  mensagemPromocional: '',
  imagemPromocionalUrl: '',
  galeriaPromocional: [],
  tempoProtecaoTelaSegundos: 10,
  precoPorFilial: false,
  permitirEstoqueNegativoGerencial: false,
  tipoCalculoComissao: 0,
});

const THEME_OPTIONS = [
  { value: 'Verde', primary: '#53CD9D', secondary: '#2FB591' },
  { value: 'Vermelho', primary: '#CD6357', secondary: '#BE484A' },
  { value: 'Azul Turquesa', primary: '#53AFCD', secondary: '#2C97AB' },
  { value: 'Cinza', primary: '#778291', secondary: '#636E7D' },
  { value: 'Laranja', primary: '#D2691E', secondary: '#FFA07A' },
  { value: 'Lilás', primary: '#A457CD', secondary: '#9B28BE' },
  { value: 'Rosa', primary: '#CD57CC', secondary: '#BE298F' },
  { value: 'Azul', primary: '#0495F0', secondary: '#0446F2' },
  { value: 'Amarelo', primary: '#CDBE57', secondary: '#BEA931' },
  { value: 'Borgonha', primary: '#800020', secondary: '#C04000' },
  { value: 'Esmeralda', primary: '#55CD6F', secondary: '#36BE4D' },
  { value: 'Ametista', primary: '#9966CC', secondary: '#E0B0FF' },
  { value: 'Cobalto', primary: '#0047AB', secondary: '#3D59AB' },
  { value: 'Ocre', primary: '#CC7722', secondary: '#FFD700' },
  { value: 'Berinjela', primary: '#8B769A', secondary: '#7E6087' },
];

const AMBIENTES = [
  { value: 1, label: 'Produção (1)' },
  { value: 2, label: 'Homologação (2)' },
];

const BALANCA_TIPOS = [
  { value: 0, label: 'Preço (0)' },
  { value: 1, label: 'Peso (1)' },
];

function SectionCard({ title, description, children, action }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function validateClient(form) {
  if (Number(form.nfceSerie) <= 0 || Number(form.nfeSerie) <= 0) {
    return 'As séries de NFC-e e NF-e devem ser positivas.';
  }
  if (Number(form.nfceSequencia) <= 0 || Number(form.nfeSequencia) <= 0) {
    return 'As sequências de NFC-e e NF-e devem ser positivas.';
  }
  const nfceAmb = Number(form.nfceTipoAmbiente) === 0 ? 2 : Number(form.nfceTipoAmbiente);
  const nfeAmb = Number(form.nfeTipoAmbiente) === 0 ? 2 : Number(form.nfeTipoAmbiente);
  if (![1, 2].includes(nfceAmb) || ![1, 2].includes(nfeAmb)) {
    return 'O ambiente fiscal deve ser 1 (produção) ou 2 (homologação).';
  }
  const digitos = Number(form.balancaQtdDigitos);
  if (digitos < 4 || digitos > 12) {
    return 'A quantidade de dígitos da balança deve estar entre 4 e 12.';
  }
  if (![0, 1].includes(Number(form.balancaTipoCodigo))) {
    return 'O tipo de código da balança deve ser 0 (preço) ou 1 (peso).';
  }
  const dia = Number(form.diaEnvioXml);
  if (dia < 1 || dia > 31) {
    return 'O dia de envio de XML deve estar entre 1 e 31.';
  }
  const tempoProtecao = Number(form.tempoProtecaoTelaSegundos);
  if (tempoProtecao < 5 || tempoProtecao > 600) {
    return 'O tempo da tela promocional deve estar entre 5 e 600 segundos.';
  }
  return null;
}

export default function FiscalSettingsPage({
  estabelecimentoId,
  embedded = false,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [cscConfigurado, setCscConfigurado] = useState(false);
  const [clientSecretConfigurado, setClientSecretConfigurado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [certificado, setCertificado] = useState(null);
  const [arquivoCertificado, setArquivoCertificado] = useState(null);
  const [senhaCertificado, setSenhaCertificado] = useState('');
  const [savingCertificado, setSavingCertificado] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoSistemaConfigurado, setLogoSistemaConfigurado] = useState(false);
  const [removerLogo, setRemoverLogo] = useState(false);

  const load = useCallback(async () => {
    if (!estabelecimentoId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await getConfiguracao(estabelecimentoId);
      setForm({
        nfceSerie: Number(data.nfceSerie ?? 1),
        nfceSequencia: Number(data.nfceSequencia ?? 1),
        nfceTipoAmbiente: Number(data.nfceTipoAmbiente ?? 2) || 2,
        nfceIdToken: data.nfceIdToken ?? '',
        nfceCsc: data.nfceCsc ?? '',
        nfeSerie: Number(data.nfeSerie ?? 1),
        nfeSequencia: Number(data.nfeSequencia ?? 1),
        nfeTipoAmbiente: Number(data.nfeTipoAmbiente ?? 2) || 2,
        emiteNfePj: Boolean(data.emiteNfePj),
        enviaEmailNfe: Boolean(data.enviaEmailNfe),
        enviaXmlAutomaticamente: Boolean(data.enviaXmlAutomaticamente),
        emailContador: data.emailContador ?? '',
        diaEnvioXml: Number(data.diaEnvioXml ?? 1),
        cnpjCredenciadora: data.cnpjCredenciadora ?? '',
        balancaQtdDigitos: Number(data.balancaQtdDigitos ?? 6),
        balancaTipoCodigo: Number(data.balancaTipoCodigo ?? 0),
        integradoZenTag: Boolean(data.integradoZenTag),
        clientIdZenTag: data.clientIdZenTag ?? '',
        clientSecretZenTag: '',
        tema: data.tema || 'Azul',
        logoSistemaBase64: null,
        mensagemPromocional: data.mensagemPromocional ?? '',
        imagemPromocionalUrl: data.imagemPromocionalUrl ?? '',
        galeriaPromocional: parseGaleria(
          data.galeriaPromocional,
          data.imagemPromocionalUrl,
        ),
        tempoProtecaoTelaSegundos: Number(data.tempoProtecaoTelaSegundos ?? 10) || 10,
        precoPorFilial: Boolean(data.precoPorFilial),
        permitirEstoqueNegativoGerencial: Boolean(data.permitirEstoqueNegativoGerencial),
        tipoCalculoComissao: Number(data.tipoCalculoComissao ?? 0),
      });
      setCscConfigurado(Boolean(data.cscConfigurado));
      setClientSecretConfigurado(Boolean(data.clientSecretZenTagConfigurado));
      setLogoSistemaConfigurado(Boolean(data.logoSistemaConfigurado));
      setRemoverLogo(false);
      setLogoPreview(
        data.logoSistemaBase64
          ? `data:image/png;base64,${data.logoSistemaBase64}`
          : '',
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar as configurações fiscais.'));
    } finally {
      setLoading(false);
    }
  }, [estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCertificado = useCallback(async () => {
    if (!estabelecimentoId) return;
    try {
      const { data } = await getCertificadoFiscal(estabelecimentoId);
      setCertificado(data);
    } catch (err) {
      setCertificado({
        configurado: false,
        mensagem: getApiErrorMessage(err, 'Não foi possível consultar o certificado.'),
      });
    }
  }, [estabelecimentoId]);

  useEffect(() => {
    loadCertificado();
  }, [loadCertificado]);

  const salvarCertificado = async () => {
    if (!arquivoCertificado) {
      setError('Selecione o arquivo .pfx.');
      return;
    }
    if (!senhaCertificado) {
      setError('Informe a senha do certificado.');
      return;
    }
    setError('');
    setSuccess('');
    setSavingCertificado(true);
    try {
      const { data } = await uploadCertificadoFiscal(
        estabelecimentoId,
        arquivoCertificado,
        senhaCertificado,
      );
      setCertificado(data);
      setArquivoCertificado(null);
      setSenhaCertificado('');
      setSuccess('Certificado enviado à ZenFiscal com sucesso.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao enviar o certificado.'));
    } finally {
      setSavingCertificado(false);
    }
  };

  const onChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onNumberChange = (field) => (e) => {
    const raw = e.target.value;
    setForm((prev) => ({ ...prev, [field]: raw === '' ? '' : Number(raw) }));
  };

  const onSelectNumber = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: Number(e.target.value) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateClient(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nfceSerie: Number(form.nfceSerie),
        nfceSequencia: Number(form.nfceSequencia),
        nfceTipoAmbiente: Number(form.nfceTipoAmbiente) || 2,
        nfceIdToken: form.nfceIdToken?.trim() || null,
        // vazio/null preserva o secret no backend
        nfceCsc: form.nfceCsc?.trim() || null,
        nfeSerie: Number(form.nfeSerie),
        nfeSequencia: Number(form.nfeSequencia),
        nfeTipoAmbiente: Number(form.nfeTipoAmbiente) || 2,
        emiteNfePj: Boolean(form.emiteNfePj),
        enviaEmailNfe: Boolean(form.enviaEmailNfe),
        enviaXmlAutomaticamente: Boolean(form.enviaXmlAutomaticamente),
        emailContador: form.emailContador?.trim() || null,
        diaEnvioXml: Number(form.diaEnvioXml),
        cnpjCredenciadora: form.cnpjCredenciadora?.trim() || null,
        balancaQtdDigitos: Number(form.balancaQtdDigitos),
        balancaTipoCodigo: Number(form.balancaTipoCodigo),
        integradoZenTag: Boolean(form.integradoZenTag),
        clientIdZenTag: form.clientIdZenTag?.trim() || null,
        clientSecretZenTag: form.clientSecretZenTag?.trim() || null,
        tema: form.tema || 'Azul',
        logoSistemaBase64: removerLogo
          ? ''
          : form.logoSistemaBase64 || null,
        mensagemPromocional: form.mensagemPromocional?.trim() || null,
        imagemPromocionalUrl: (() => {
          const first = (form.galeriaPromocional || []).find((item) =>
            String(item.url || '').trim(),
          );
          const url = String(first?.url || '').trim();
          if (!url || url.startsWith('data:')) return null;
          return url;
        })(),
        galeriaPromocional: JSON.stringify(
          (form.galeriaPromocional || [])
            .map((item) => ({
              url: String(item.url || '').trim(),
              tipo: item.tipo === 'video' ? 'video' : detectarTipoMidia(item.url),
            }))
            .filter((item) => item.url),
        ),
        tempoProtecaoTelaSegundos: Number(form.tempoProtecaoTelaSegundos) || 10,
        precoPorFilial: Boolean(form.precoPorFilial),
        permitirEstoqueNegativoGerencial: Boolean(form.permitirEstoqueNegativoGerencial),
        tipoCalculoComissao: Number(form.tipoCalculoComissao ?? 0),
      };

      const { data } = await updateConfiguracao(estabelecimentoId, payload);
      setCscConfigurado(Boolean(data.cscConfigurado));
      setClientSecretConfigurado(Boolean(data.clientSecretZenTagConfigurado));
      setLogoSistemaConfigurado(Boolean(data.logoSistemaConfigurado));
      setRemoverLogo(false);
      setLogoPreview(
        data.logoSistemaBase64
          ? `data:image/png;base64,${data.logoSistemaBase64}`
          : '',
      );
      setForm((prev) => ({
        ...prev,
        nfceCsc: data.nfceCsc ?? prev.nfceCsc,
        clientSecretZenTag: '',
        logoSistemaBase64: null,
        nfceIdToken: data.nfceIdToken ?? '',
        clientIdZenTag: data.clientIdZenTag ?? '',
        tema: data.tema || prev.tema,
        mensagemPromocional: data.mensagemPromocional ?? '',
        imagemPromocionalUrl: data.imagemPromocionalUrl ?? '',
        galeriaPromocional: parseGaleria(
          data.galeriaPromocional,
          data.imagemPromocionalUrl,
        ),
        tempoProtecaoTelaSegundos:
          Number(data.tempoProtecaoTelaSegundos ?? prev.tempoProtecaoTelaSegundos ?? 10) || 10,
        precoPorFilial: Boolean(data.precoPorFilial),
        permitirEstoqueNegativoGerencial: Boolean(data.permitirEstoqueNegativoGerencial),
        tipoCalculoComissao: Number(data.tipoCalculoComissao ?? prev.tipoCalculoComissao ?? 0),
      }));
      setSuccess('Configurações salvas com sucesso.');
      if (typeof onSaved === 'function') {
        onSaved('Configurações salvas com sucesso.');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar as configurações.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{
      p: embedded ? 0 : { xs: 2, md: 3 },
      maxWidth: embedded ? 'none' : 1100,
      mx: 'auto',
    }}>
      {!embedded && (
        <PageHeader
          title="Configurações do estabelecimento"
          subtitle="Aparência dos PDVs, NFC-e/NF-e, automação, balança e ZenTag."
          actions={
            <Button
              type="submit"
              form="fiscal-settings-form"
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={loading || saving}
            >
              Salvar
            </Button>
          }
        />
      )}

      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && (
        <Box component="form" id="fiscal-settings-form" onSubmit={onSubmit} autoComplete="off">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <SectionCard
            title="Aparência dos PDVs"
            description="Tema, logo e galeria promocional da tela de descanso — valem para todos os PDVs deste estabelecimento após sincronizar."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="tema-pdv-label">Tema</InputLabel>
                  <Select
                    labelId="tema-pdv-label"
                    label="Tema"
                    value={form.tema || 'Azul'}
                    onChange={onChange('tema')}
                  >
                    {THEME_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: opt.primary }} />
                          <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: opt.secondary }} />
                          <span>{opt.value}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Cor principal do ZenPDV (Android e Windows).
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  {logoPreview ? (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Logo"
                      sx={{
                        width: 72,
                        height: 72,
                        objectFit: 'contain',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                    />
                  ) : null}
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                  >
                    Logo
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          const result = String(reader.result || '');
                          setLogoPreview(result);
                          setRemoverLogo(false);
                          setForm((prev) => ({
                            ...prev,
                            logoSistemaBase64: result.includes(',')
                              ? result.split(',')[1]
                              : result,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </Button>
                  {(logoPreview || logoSistemaConfigurado) && (
                    <Button
                      color="inherit"
                      onClick={() => {
                        setLogoPreview('');
                        setRemoverLogo(true);
                        setForm((prev) => ({ ...prev, logoSistemaBase64: null }));
                      }}
                    >
                      Remover logo
                    </Button>
                  )}
                </Stack>
                <FormHelperText sx={{ mt: 1 }}>
                  Nos relatórios e impressões do Gestão. Não aparece na barra do sistema.
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Mensagem promocional"
                  value={form.mensagemPromocional}
                  onChange={onChange('mensagemPromocional')}
                  fullWidth
                  multiline
                  minRows={2}
                  helperText="Sobreposta na parte inferior da tela de descanso."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tempo da tela promocional (segundos)"
                  type="number"
                  value={form.tempoProtecaoTelaSegundos}
                  onChange={onChange('tempoProtecaoTelaSegundos')}
                  fullWidth
                  inputProps={{ min: 5, max: 600, step: 1 }}
                  helperText="Inatividade no caixa vazio antes de exibir a proteção de tela (5–600 s)."
                />
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Galeria promocional
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Imagens e vídeos em sequência na tela de descanso. Até{' '}
                      {GALERIA_MAX_ITENS} itens · imagem embutida ≤{' '}
                      {GALERIA_MAX_IMAGEM_MB} MB · vídeo só por URL (arquivo ≤{' '}
                      {GALERIA_MAX_VIDEO_MB} MB no host).
                    </Typography>
                  </Box>
                  {(form.galeriaPromocional || []).map((item, index) => {
                    const url = String(item.url || '').trim();
                    const isVideo = item.tipo === 'video';
                    const showThumb = Boolean(url) && !isVideo;
                    return (
                    <Stack
                      key={`galeria-${index}`}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                    >
                      {showThumb ? (
                        <Box
                          component="img"
                          src={url}
                          alt={`Mídia ${index + 1}`}
                          sx={{
                            width: 72,
                            height: 72,
                            objectFit: 'contain',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            flexShrink: 0,
                          }}
                        />
                      ) : url && isVideo ? (
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            flexShrink: 0,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Vídeo
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            flexShrink: 0,
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                          }}
                        />
                      )}
                      <TextField
                        label={`Mídia ${index + 1} (URL)`}
                        value={url.startsWith('data:') ? '' : url}
                        placeholder={
                          url.startsWith('data:')
                            ? `Arquivo embutido (${item.tipo})`
                            : 'https://…'
                        }
                        onChange={(e) => {
                          const nextUrl = e.target.value;
                          setForm((prev) => {
                            const next = [...(prev.galeriaPromocional || [])];
                            next[index] = {
                              url: nextUrl,
                              tipo: detectarTipoMidia(nextUrl),
                            };
                            return { ...prev, galeriaPromocional: next };
                          });
                        }}
                        fullWidth
                        helperText={
                          url.startsWith('data:')
                            ? `Arquivo local embutido · ${item.tipo}`
                            : isVideo
                              ? 'Vídeo — toca inteiro e passa para o próximo'
                              : 'Imagem — ~8s por slide'
                        }
                      />
                      <Chip
                        size="small"
                        label={isVideo ? 'Vídeo' : 'Imagem'}
                        color={isVideo ? 'secondary' : 'default'}
                      />
                      <IconButton
                        aria-label="Remover mídia"
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            galeriaPromocional: (prev.galeriaPromocional || []).filter(
                              (_, i) => i !== index,
                            ),
                          }));
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                    );
                  })}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      disabled={(form.galeriaPromocional || []).length >= GALERIA_MAX_ITENS}
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          galeriaPromocional: [
                            ...(prev.galeriaPromocional || []),
                            { url: '', tipo: 'imagem' },
                          ],
                        }));
                      }}
                    >
                      Adicionar URL
                    </Button>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      disabled={(form.galeriaPromocional || []).length >= GALERIA_MAX_ITENS}
                    >
                      Enviar imagem
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          if (!file.type.startsWith('image/')) {
                            setError('Só é possível embutir imagens. Para vídeo, use uma URL.');
                            return;
                          }
                          const maxBytes = GALERIA_MAX_IMAGEM_MB * 1024 * 1024;
                          if (file.size > maxBytes) {
                            setError(`Imagem excede o teto de ${GALERIA_MAX_IMAGEM_MB} MB.`);
                            return;
                          }
                          if ((form.galeriaPromocional || []).length >= GALERIA_MAX_ITENS) {
                            setError(`Máximo de ${GALERIA_MAX_ITENS} itens na galeria.`);
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => {
                            const result = String(reader.result || '');
                            setError('');
                            setForm((prev) => ({
                              ...prev,
                              galeriaPromocional: [
                                ...(prev.galeriaPromocional || []),
                                { url: result, tipo: 'imagem' },
                              ],
                            }));
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="Certificado digital A1"
            description="O PFX é validado no servidor e enviado diretamente à ZenFiscal; não fica armazenado no navegador nem no ZenGestão."
            action={
              certificado?.configurado ? (
                <Chip size="small" color="success" label="Certificado configurado" />
              ) : (
                <Chip size="small" variant="outlined" label="Não configurado" />
              )
            }
          >
            {certificado?.mensagem && (
              <Alert
                severity={certificado.configurado ? 'success' : 'info'}
                sx={{ mb: 2 }}
              >
                {certificado.mensagem}
              </Alert>
            )}
            {certificado?.validoAte && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Titular: {certificado.titular || 'não informado'} · válido até{' '}
                {new Date(certificado.validoAte).toLocaleDateString('pt-BR')}
              </Typography>
            )}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                >
                  {arquivoCertificado?.name || 'Selecionar certificado .pfx'}
                  <input
                    hidden
                    type="file"
                    accept=".pfx,application/x-pkcs12"
                    onChange={(e) => setArquivoCertificado(e.target.files?.[0] || null)}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Senha do certificado"
                  type="password"
                  value={senhaCertificado}
                  onChange={(e) => setSenhaCertificado(e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  onClick={salvarCertificado}
                  disabled={savingCertificado}
                  startIcon={
                    savingCertificado
                      ? <CircularProgress size={16} color="inherit" />
                      : <UploadFileIcon />
                  }
                  fullWidth
                >
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="NFC-e"
            description="Série, sequência, ambiente e token CSC."
            action={
              cscConfigurado ? (
                <Chip size="small" color="success" label="CSC configurado" />
              ) : (
                <Chip size="small" variant="outlined" label="CSC não configurado" />
              )
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Série"
                  type="number"
                  value={form.nfceSerie}
                  onChange={onNumberChange('nfceSerie')}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Sequência"
                  type="number"
                  value={form.nfceSequencia}
                  onChange={onNumberChange('nfceSequencia')}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                  helperText="Próximo número a emitir"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="nfce-ambiente-label">Ambiente</InputLabel>
                  <Select
                    labelId="nfce-ambiente-label"
                    label="Ambiente"
                    value={form.nfceTipoAmbiente}
                    onChange={onSelectNumber('nfceTipoAmbiente')}
                  >
                    {AMBIENTES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="IdToken"
                  value={form.nfceIdToken}
                  onChange={onChange('nfceIdToken')}
                  fullWidth
                  inputProps={{ autoComplete: 'off' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="CSC"
                  value={form.nfceCsc}
                  onChange={onChange('nfceCsc')}
                  fullWidth
                  inputProps={{ autoComplete: 'off', spellCheck: false }}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="NF-e" description="Parâmetros de emissão de nota fiscal eletrônica modelo 55.">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Série"
                  type="number"
                  value={form.nfeSerie}
                  onChange={onNumberChange('nfeSerie')}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Sequência"
                  type="number"
                  value={form.nfeSequencia}
                  onChange={onNumberChange('nfeSequencia')}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="nfe-ambiente-label">Ambiente</InputLabel>
                  <Select
                    labelId="nfe-ambiente-label"
                    label="Ambiente"
                    value={form.nfeTipoAmbiente}
                    onChange={onSelectNumber('nfeTipoAmbiente')}
                  >
                    {AMBIENTES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.emiteNfePj}
                      onChange={onChange('emiteNfePj')}
                    />
                  }
                  label="Emite NF-e para PJ"
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Automação" description="Envio de e-mail e XML ao contador.">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={<Checkbox checked={form.enviaEmailNfe} onChange={onChange('enviaEmailNfe')} />}
                  label="Envia e-mail da NF-e"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.enviaXmlAutomaticamente}
                      onChange={onChange('enviaXmlAutomaticamente')}
                    />
                  }
                  label="Envia XML automaticamente"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="E-mail do contador"
                  type="email"
                  value={form.emailContador}
                  onChange={onChange('emailContador')}
                  fullWidth
                  inputProps={{ autoComplete: 'email' }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Dia envio XML"
                  type="number"
                  value={form.diaEnvioXml}
                  onChange={onNumberChange('diaEnvioXml')}
                  fullWidth
                  inputProps={{ min: 1, max: 31 }}
                  helperText="Entre 1 e 31"
                />
              </Grid>
              <Grid item xs={12} sm={8} md={3}>
                <TextField
                  label="CNPJ credenciadora"
                  value={form.cnpjCredenciadora}
                  onChange={onChange('cnpjCredenciadora')}
                  fullWidth
                  inputProps={{ autoComplete: 'off' }}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="Política de preços"
            description="Define se o preço do produto é o mesmo em todas as filiais ou pode variar por estabelecimento. Aplica-se a todo o tenant."
          >
            <FormControl component="fieldset">
              <FormLabel component="legend">Como os preços são tratados</FormLabel>
              <RadioGroup
                value={form.precoPorFilial ? 'filial' : 'global'}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    precoPorFilial: e.target.value === 'filial',
                  }));
                }}
              >
                <FormControlLabel
                  value="global"
                  control={<Radio />}
                  label="Preço global — ao salvar um produto, o valor é replicado para todas as filiais"
                />
                <FormControlLabel
                  value="filial"
                  control={<Radio />}
                  label="Preço por filial — cada estabelecimento mantém seus próprios preços"
                />
              </RadioGroup>
              <FormHelperText>
                No PDV e na carga da balança, só entram os preços da filial do terminal.
              </FormHelperText>
            </FormControl>
          </SectionCard>

          <SectionCard
            title="Estoque gerencial"
            description="Movimentações de saída e romaneios entre filiais no Gestão."
          >
            <FormControlLabel
              control={(
                <Checkbox
                  checked={Boolean(form.permitirEstoqueNegativoGerencial)}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    permitirEstoqueNegativoGerencial: e.target.checked,
                  }))}
                />
              )}
              label="Permitir estoque negativo nas movimentações gerenciais (com confirmação)"
            />
            <FormHelperText>
              Quando ativo, saídas e separação de romaneios que deixariam o saldo negativo pedem confirmação antes de prosseguir. Vendas no PDV não são afetadas.
            </FormHelperText>
          </SectionCard>

          <SectionCard
            title="Comissão de vendedores"
            description="Define a base exclusiva do percentual de comissão nas vendas. Aplica-se a todo o tenant."
          >
            <FormControl component="fieldset">
              <FormLabel component="legend">Calcular comissão por</FormLabel>
              <RadioGroup
                value={String(form.tipoCalculoComissao ?? 0)}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    tipoCalculoComissao: Number(e.target.value),
                  }));
                }}
              >
                <FormControlLabel
                  value="0"
                  control={<Radio />}
                  label="Vendedor — % do cadastro do usuário (Considerar vendedor)"
                />
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label="Produto — % do cadastro do produto"
                />
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label="Categoria — % da categoria do produto"
                />
              </RadioGroup>
              <FormHelperText>
                Em cada venda, o % é aplicado sobre o valor líquido do item e gravado no histórico.
              </FormHelperText>
            </FormControl>
          </SectionCard>

          <SectionCard
            title="Balança"
            description="Códigos EAN-13 com prefixo 2 (produto de balança): dígitos do código interno e se o valor embutido é preço ou peso."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Qtd. dígitos do produto"
                  type="number"
                  value={form.balancaQtdDigitos}
                  onChange={onNumberChange('balancaQtdDigitos')}
                  fullWidth
                  required
                  inputProps={{ min: 4, max: 12 }}
                  helperText="Entre 4 e 12"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="balanca-tipo-label">Tipo de código</InputLabel>
                  <Select
                    labelId="balanca-tipo-label"
                    label="Tipo de código"
                    value={form.balancaTipoCodigo}
                    onChange={onSelectNumber('balancaTipoCodigo')}
                  >
                    {BALANCA_TIPOS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Define se os dígitos após o código do produto representam preço ou peso.
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="ZenTag"
            description="Integração com a plataforma ZenTag."
            action={
              clientSecretConfigurado ? (
                <Chip size="small" color="success" label="Client secret configurado" />
              ) : (
                <Chip size="small" variant="outlined" label="Client secret não configurado" />
              )
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.integradoZenTag}
                      onChange={onChange('integradoZenTag')}
                    />
                  }
                  label="Integração ZenTag ativa"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Client ID"
                  value={form.clientIdZenTag}
                  onChange={onChange('clientIdZenTag')}
                  fullWidth
                  inputProps={{ autoComplete: 'off' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Client secret"
                  type="password"
                  value={form.clientSecretZenTag}
                  onChange={onChange('clientSecretZenTag')}
                  fullWidth
                  placeholder={
                    clientSecretConfigurado
                      ? 'Deixe vazio para preservar o atual'
                      : 'Informe o client secret'
                  }
                  helperText="Campo vazio não altera o secret já salvo no servidor."
                  inputProps={{ autoComplete: 'new-password' }}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1, mb: 4 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              sx={{ minWidth: 160 }}
            >
              Salvar configurações
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
